/* eslint-disable no-undef */
import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Marker } from '@react-google-maps/api';
import { styled } from '@mui/material';
import SuggestByType from 'js/components/common/SuggestByType';
import { SLAS_PLACEHOLDER } from 'js/helpers/Localization';
import { COUNTRY } from 'js/constants/crux';
import ErrorMessages from 'js/constants/errorMsg';
import SuggestionTypes from 'js/constants/suggestionTypes';
import { clearSuggestion } from 'js/actions/clapi';
import { MAP_MARKER_IMAGE_NAME } from 'js/constants/map';
import { getAssetDomainOld } from 'js/constants/assets';

export const SEARCH_STATE = {
    hidden: 'hidden',
    visible: 'visible',
};

const ZOOM_BY_LOCATION_TYPE = {
    [SuggestionTypes.LOCALITY]: 12,
    [SuggestionTypes.COUNCIL_AREA]: 7,
    [SuggestionTypes.SUBURB]: 12,
    [SuggestionTypes.STATE]: 7,
    [SuggestionTypes.TERRITORIAL_AUTHORITY]: 7,
    [SuggestionTypes.POSTCODE]: 12,
    defaultZoom: 16,
};

const Container = styled('div')(({ state }) => ({
    transition: 'width 0.2s ease-in-out',
    width: state === SEARCH_STATE.visible ? 460 : 0,
    overflow: state,
}));

const Search = (props) => {
    const [selectedSuggestion, setSelectedSuggestion] = useState({});
    const [coordinates, setCoordinates] = useState(null);
    const geocoder = useRef(new window.google.maps.Geocoder());

    const dispatch = useDispatch();
    const store = useSelector(state => ({
        suggestions: state.clapi.get('suggestions'),
        isSearchSuggestionsLoading: state
            .componentAccess
            .get('searchSuggestions')
            .isLoadingSuggestion,
        searchSuggestionsHasError: state.componentAccess.get('searchSuggestions').hasError,
    }));

    const suggestionSelect = (location) => {
        setSelectedSuggestion(location);
        geocoder.current.geocode({
            address: location.suggestion, componentRestrictions: { country: COUNTRY },
        }, (results, status) => {
            const zoom = ZOOM_BY_LOCATION_TYPE[location.suggestionType]
                ?? ZOOM_BY_LOCATION_TYPE.defaultZoom
            if (status === 'OK') {
                const targetLocation = results[0].geometry.location;
                props.map.panTo(targetLocation);
                props.map.setZoom(zoom);
                setCoordinates(targetLocation);
            } else {
                props.openSnackBar('error', ErrorMessages.FAILED_TO_FETCH);
            }
        });
    };

    useEffect(() => {
        // cleaning up suggestions when unmounting
        return () => {
            if (store.suggestions.length) {
                dispatch(clearSuggestion());
            }
        }
    }, []);

    return (<>
        <Container state={props.searchState}>
            <SuggestByType
                selectedSuggestion={selectedSuggestion}
                setSelectedSuggestion={suggestionSelect}
                suggestions={store.suggestions}
                isSearchSuggestionsLoading={store.isSearchSuggestionsLoading}
                searchSuggestionsHasError={store.searchSuggestionsHasError}
                name="suburb-postcode"
                placeholderText={SLAS_PLACEHOLDER}
            />
        </Container>
        {
            coordinates &&
            <Marker
                position={coordinates}
                icon={{
                    url: getAssetDomainOld(MAP_MARKER_IMAGE_NAME.TARGET_PROPERTY_INACTIVE_PIN),
                    scaledSize: new google.maps.Size(35, 35),
                    origin: new google.maps.Point(-5, 0),
                }}
                zIndex={1}
            />
        }
    </>);
};

Search.propTypes = {
    searchState: PropTypes.oneOf(Object.values(SEARCH_STATE)),
    map: PropTypes.object.isRequired,
}

export default Search;
